import { Editor, type Node, Element as SlateElement, Transforms } from 'slate'

import { isBlockActive } from './block'

function isMathNode(n: Node): n is SlateElement & { type: 'math' } {
  return SlateElement.isElement(n) && n.type === 'math'
}

export type MathElementDetails = Pick<SlateElement & { type: 'math' }, 'latex' | 'displayMode'>

export const DEFAULT_MATH_DETAILS: MathElementDetails = { latex: 'x^2', displayMode: false }

export function getActiveMathElementDetails(editor: Editor): MathElementDetails | null {
  const { selection } = editor
  if (selection == null) {
    return null
  }
  const [math] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: isMathNode,
  })
  if (math == null) {
    return null
  }
  const [mathNode] = math
  return { latex: mathNode.latex, displayMode: mathNode.displayMode }
}

export function removeMath(editor: Editor): void {
  Transforms.removeNodes(editor, { match: isMathNode })
}

export function isMathActive(editor: Editor): boolean {
  return isBlockActive(editor, 'math')
}

export function insertMath(editor: Editor, details: MathElementDetails): void {
  const math: SlateElement = {
    type: 'math',
    latex: details.latex,
    displayMode: details.displayMode,
    children: [{ text: '' }],
  }
  Transforms.insertNodes(editor, math)
}

export function setMathElementDetails(editor: Editor, details: MathElementDetails): void {
  if (isMathActive(editor)) {
    Transforms.setNodes(
      editor,
      {
        latex: details.latex,
        displayMode: details.displayMode,
      },
      { match: isMathNode }
    )
  } else {
    insertMath(editor, details)
  }
}

export function withMath(editor: Editor): Editor {
  const { isVoid } = editor

  // eslint-disable-next-line no-param-reassign
  editor.isVoid = element => (element.type === 'math' ? true : isVoid(element))

  return editor
}
